import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpIcon from '@mui/icons-material/Help';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { Box, FormHelperText } from '@mui/material';

import { useApp } from 'contexts/AppContext';
import { useTheme } from 'contexts/ThemeContext';

import { capitalize } from 'utils';
import { defaultModule } from 'utils/constants/domainModules';
import {
  formatListFieldUser,
  serializePlatformAccount,
  serializePlatformPage
} from 'utils/functions';
import { DOMAINCONFIG, FUNNEL, USERS } from 'utils/rest';

import { HelpTooltip, Text, Tooltip } from 'components/DataDisplay';
import Image from 'components/Image';
import {
  AsyncAutocomplete,
  AutocompleteTextField,
  Button,
  ColorPicker,
  CustomSelect,
  DoubleConditionalListField,
  IconButton,
  MultipleSelect,
  Select,
  Switch,
  TextField
} from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { ListField } from 'components/ListField';
import { Loading } from 'components/Loading';

import {
  ACTIVATION_REQUIRED_FIELD_OPTIONS,
  flattenDataProviders,
  formatActivableMediaAutomationConfigs,
  formatActivationRequiredMediaConfigs,
  mapActivableMediaAutomationConfigsFromConfig,
  mapActivationRequiredMediaConfigsFromConfig
} from '../../utils';
import { DomainPageActionBar } from '../DomainPageActionBar';
import { DomainPageSection as FormSection } from '../DomainPageSection';

const formatModule = () => {
  const formated = {};
  Object.keys(defaultModule).forEach(key => {
    formated[key] = defaultModule[key].state;
  });
  return formated;
};

const campaignStartDayRanges = Array.from({ length: 21 }, (_, i) => {
  const value = i;
  return { value, label: value.toString() };
});

const delayDurationRanges = [
  { value: 'day', label: 'Jour' },
  { value: 'week', label: 'Sem' },
  { value: 'month', label: 'Mois' }
];

const geoZones = [
  { value: 'postalCode', label: 'Code postal' },
  { value: 'radius', label: 'Rayon' }
];

const flattenDefaultTargetingTypesByMedia = value => {
  return (value || []).reduce((acc, row) => {
    const medias = Array.isArray(row?.medias) ? row.medias : [];

    medias.forEach(media => {
      acc.push({
        media,
        targetingType: row?.targetingType
      });
    });

    return acc;
  }, []);
};

const groupDefaultTargetingTypesByMedia = value => {
  const grouped = (value || []).reduce((acc, row) => {
    if (!row?.media || !row?.targetingType) {
      return acc;
    }

    if (!acc[row.targetingType]) {
      acc[row.targetingType] = [];
    }

    acc[row.targetingType].push(row.media);
    return acc;
  }, {});

  return Object.keys(grouped).map(targetingType => ({
    medias: grouped[targetingType],
    targetingType
  }));
};

export const DomainForm = props => {
  const {
    config,
    dataProviders: dataProvidersOptions,
    preset,
    onValidate,
    onClose,
    onCreate,
    actionName,
    title,
    saveLabel,
    createLabel,
    connectorsConnexions,
    importModels,
    activableMedias,
    geoInterfaces,
    customers = [],
    shopGroups,
    profileKeys
  } = props;
  const { themeColors } = useTheme();
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [funnels, setFunnels] = useState([]);

  const timerRef = useRef(null);

  const formatColors = () => {
    const formated = {
      // eslint-disable-next-line camelcase
      primary_header: themeColors.primary_header,
      txtHeaderColor: themeColors.themeV2.white
    };
    Object.keys(defaultModule).forEach(key => {
      const { colorKey } = defaultModule[key];
      formated[colorKey] = themeColors[colorKey];
    });
    return formated;
  };

  const { domainConfig } = useApp();

  const fetchFunnels = async search => {
    const body = { search, consolidated: true };
    const found = await FUNNEL.getPaginated(body);
    const newData = found?.data || [];

    setFunnels(prev => {
      const map = new Map();
      [...prev, ...newData].forEach(f => {
        map.set(f._id, map.get(f._id) || f);
      });
      return Array.from(map.values());
    });

    return newData;
  };

  const hiddenFileLogoInput = useRef(null);
  const hiddenFileDarkLogoInput = useRef(null);
  const errorState = {
    nameError: false,
    cguError: false,
    dpaError: false,
    legalTermsError: false,
    privacyPolicyError: false,
    shopNameError: false,
    campaignCrmPresetError: false,
    defaultStripeCustomerIdError: false,
    dexemConnectionIdError: false
  };

  const [autoLoginEmailError, setAutoLoginEmailError] = useState('');
  const [token, setToken] = useState('');
  const [
    {
      nameError,
      cguError,
      dpaError,
      legalTermsError,
      privacyPolicyError,
      shopNameError,
      campaignCrmPresetError,
      defaultStripeCustomerIdError
    },
    setError
  ] = useState(errorState);

  const [
    {
      name,
      spendings,
      enabledPmlFront,
      digitaleo,
      activationEnrichedHome,
      magasinViewOnly,
      sendCreationEmailOnCampaignValidation,
      campaignCrmPreset,
      campaignCrmActivated,
      defaultStripeCustomerActive,
      defaultStripeCustomerId,
      campaignStartDayRange,
      logo,
      darkLogo,
      domain,
      dataProviders,
      excludeDataProviders,
      modules,
      emailContact,
      cgu,
      dpa,
      legalTerms,
      privacyPolicy,
      selectedPlatformAccounts,
      selectedPlatformPages,
      dataFeedbackConfigNoInvoice,
      campaignDurationAvertissement,
      minDelayDuration,
      maxDelayDuration,
      minDelayUnit,
      maxDelayUnit,
      shopName,
      groupTaxonomy,
      sendCreationEmail,
      autoLogin,
      autoLoginEmail,
      createCustomers,
      customTheme,
      activablePurchaseOrder,
      copyPal,
      pmlActivationRateBanner,
      selectedActivableMedias,
      selectedGeoInterfaces,
      isLightContent,
      shortCampaign,
      longCampaign,
      customBrevoV2ApiKey,
      customBrevoV3ApiKey,
      gettyPrivateKey,
      gettySecretKey,
      maskFees,
      enableDeals,
      budgetSmoothing,
      dexemEmails,
      dexemActivated,
      dexemConnectionId,
      autoSendInvoice,
      performanceAlert,
      toleranceLimit,
      emailPerformance,
      alertingKpi,
      alertingFrequency,
      allowSellerMail,
      selectedUsers,
      defaultParentShopGroup,
      defaultProfileKey,
      autoDeleteOldCampaigns,
      whatsappAgent,
      adminUsers,
      defaultFunnelId,
      autoCreatePresetByGroup,
      countries,
      defaultGeoZoneType,
      defaultGeoZoneValues,
      defaultTargetingTypesByMedia,
      hideFunnelBo,
      duplicateModifyCampaigns,
      campaignSponsoringMeta,
      enabledPmlRepiquage,
      activationRequiredMediaConfigs,
      activableMediaAutomationConfigs,
      budgetTypeImpressions
    },
    setState
  ] = useState({});

  const parsedShopGroups = shopGroups
    .filter(el => !el.shops || el.shops?.length === 0)
    .map(el => {
      return { label: el.name, id: el.key };
    });

  const [selectedFunnel, setSelectedFunnel] = useState(null);

  useEffect(() => {
    if (defaultFunnelId && funnels.length) {
      const match = funnels.find(f => f._id === defaultFunnelId);
      if (match) {
        setSelectedFunnel(match);
      }
    }
  }, [defaultFunnelId, funnels]);

  const toggleError = (itemKey, state) => {
    setError(prevState => {
      const errors = { ...prevState };
      errors[`${itemKey}Error`] = state;
      return errors;
    });
  };

  const getTargetableMediaOptions = () => {
    return activableMedias
      .filter(({ key, geoTargets = [] }) => {
        const isSelectedMedia =
          Array.isArray(selectedActivableMedias) &&
          selectedActivableMedias.includes(key);

        return isSelectedMedia && geoTargets.length;
      })
      .map(({ key, name: mediaName }) => ({
        key,
        label: mediaName
      }));
  };

  const getActivationRequiredMediaOptions = () => {
    const activeMediaKeys = Array.isArray(selectedActivableMedias)
      ? selectedActivableMedias
      : [];

    return activableMedias.reduce((acc, media) => {
      if (
        !activeMediaKeys.includes(media.key) ||
        !(media.page || media.canSetUp)
      ) {
        return acc;
      }

      acc.push({
        value: media.key,
        label: media.name
      });

      return acc;
    }, []);
  };

  const getActivableMediaAutomationOptions = () => {
    const activeMediaKeys = Array.isArray(selectedActivableMedias)
      ? selectedActivableMedias
      : [];

    const configuredMediaKeys = (activableMediaAutomationConfigs || []).reduce(
      (acc, row) => {
        const medias = Array.isArray(row?.activableMedias)
          ? row.activableMedias
          : [];

        medias.forEach(media => {
          const mediaKey = media?.key ?? media?.value ?? media;

          if (mediaKey) {
            acc.push(mediaKey);
          }
        });

        return acc;
      },
      []
    );

    return activableMedias.reduce((acc, media) => {
      if (
        !activeMediaKeys.includes(media.key) ||
        !media.canSetUp ||
        configuredMediaKeys.includes(media.key)
      ) {
        return acc;
      }

      acc.push({
        key: media.key,
        label: media.name
      });

      return acc;
    }, []);
  };

  const getTargetingTypeOptionsByMedia = mediaKeys => {
    const selectedMediasForTargeting = activableMedias.filter(({ key }) =>
      mediaKeys.includes(key)
    );

    if (selectedMediasForTargeting.length === 0) {
      return [];
    }

    const canUseGeo = selectedMediasForTargeting.every(media =>
      media?.geoTargets?.find(el => el.geo && !el.audience)
    );
    const canUseAudience = selectedMediasForTargeting.every(media =>
      media?.geoTargets?.find(el => !el.geo && el.audience)
    );
    const canUseGeoAndAudience = selectedMediasForTargeting.every(media =>
      media?.geoTargets?.find(el => el.geo && el.audience)
    );

    const options = [];

    if (canUseGeo) {
      options.push({ label: 'Géographique', value: 'geo' });
    }

    if (canUseAudience) {
      options.push({ label: 'Audience', value: 'audience' });
    }

    if (canUseGeoAndAudience) {
      options.push({ label: 'Géo + audience', value: 'geoAndAudience' });
    }

    return options;
  };

  const toggleTrackingType = value => {
    setState(prev => ({
      ...prev,
      alertingKpi: value ? 'kpi1' : 'budget'
    }));
  };

  const toggleFrequency = value => {
    setState(prev => ({
      ...prev,
      alertingFrequency: value ? 'weekly' : 'daily'
    }));
  };

  const checkIsAutoLogin = async () => {
    const response = await DOMAINCONFIG.checkIsAutoLogin(config?.name);
    if (Object.keys(response).length !== 0)
      setState(prevState => ({
        ...prevState,
        autoLogin: response.actif
      }));
  };
  useEffect(() => {
    checkIsAutoLogin();
    // eslint-disable-next-line
  }, []);

  const handleModulesChange = (key, value) => {
    const newModules = { ...modules };
    newModules[key] = value;

    if (key === 'activation' && value) {
      newModules.audiences = true;
    }

    if (key === 'audiences' && !value) {
      newModules.activation = false;
    }

    setState(prevState => ({
      ...prevState,
      modules: newModules
    }));
  };

  const getFileBase64 = (file, callback) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    // Since FileReader is asynchronous,
    // we need to pass data back.
    reader.onload = () => callback(reader.result);
  };

  const imageUploadCallback = file =>
    new Promise(resolve =>
      getFileBase64(file, data => resolve({ data: { link: data } }))
    );

  const handleChange = key => value => {
    if (['cgu', 'dpa', 'legalTerms', 'privacyPolicy'].includes(key)) {
      toggleError(key, false);
    }
    if (key === 'autoLoginEmail') {
      setAutoLoginEmailError('');
    }
    setState(prevState => {
      const states = { ...prevState };
      if (key === 'shopName') {
        states[key] = {
          level: 'shop',
          name: value,
          key: 'shop'
        };
      } else {
        states[key] = value;
      }
      return states;
    });
  };

  const handleChangeColor = key => value => {
    setState(prevState => {
      const states = { ...prevState };
      if (!states.customTheme) {
        states.customTheme = {};
      }
      states.customTheme[key] = value;
      return states;
    });
  };

  const handleChangeGroupTaxonomyAdd = value => {
    const updatedValue = value.map(item => {
      const itemName = item.name || '';
      const key = itemName
        .replace(/[^\w\s]/gi, '')
        .toLowerCase()
        .replace(/\s/g, '');
      return { ...item, key };
    });

    setState(prevState => {
      const states = { ...prevState };
      states.groupTaxonomy = updatedValue;
      return states;
    });
  };

  const handleLogoChange = key => async event => {
    const fileUploaded = event.target.files[0];
    const { data } = await imageUploadCallback(fileUploaded);
    handleChange(key)(data?.link);
  };

  const handleChangePlatformAccount = value => {
    setState(prevState => ({
      ...prevState,
      selectedPlatformAccounts: value.map(el => {
        let { connexionId } = el;
        if (typeof el.connexionId === 'string') {
          connexionId = {
            value: el.connexionId,
            label: connectorsConnexions.find(e => e.value === el.connexionId)
              ?.label
          };
        }
        return {
          ...el,
          connexionId
        };
      })
    }));
  };

  const handleChangePlatformPage = value => {
    setState(prevState => ({
      ...prevState,
      selectedPlatformPages: value.map(el => {
        let { connexionId } = el;
        if (typeof el.connexionId === 'string') {
          connexionId = {
            value: el.connexionId,
            label: connectorsConnexions.find(e => e.value === el.connexionId)
              ?.label
          };
        }
        return {
          ...el,
          connexionId
        };
      })
    }));
  };

  const matchRegex = url => {
    return !/^https:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/[^\s]*)?$/.test(url);
  };

  const doAction = () => {
    const isEmptyDataProviders = dataProviders.length === 0;
    const isEmptyExcludeDataProviders = excludeDataProviders.length === 0;
    let error = false;
    if (cgu && matchRegex(cgu)) {
      toggleError('cgu', true);
      error = true;
    }
    if (dpa && matchRegex(dpa)) {
      toggleError('dpa', true);
      error = true;
    }
    if (legalTerms && matchRegex(legalTerms)) {
      toggleError('legalTerms', true);
      error = true;
    }
    if (privacyPolicy && matchRegex(privacyPolicy)) {
      toggleError('privacyPolicy', true);
      error = true;
    }
    if (campaignCrmActivated && !campaignCrmPreset) {
      toggleError('campaignCrmPreset', true);
      error = true;
    }

    if (defaultStripeCustomerActive && !defaultStripeCustomerId) {
      toggleError('defaultStripeCustomerId', true);
      error = true;
    }
    if (dexemActivated && !dexemConnectionId) {
      toggleError('dexemConnectionId', true);
      error = true;
    }
    const emails =
      dexemEmails?.map(el => {
        return el.email;
      }) || [];
    const datas = {
      name,
      autoLogin,
      campaignCrm: {
        active: campaignCrmActivated,
        presetId: campaignCrmPreset
      },
      defaultStripeCustomer: {
        active: defaultStripeCustomerActive,
        id: defaultStripeCustomerId
      },
      dexem: dexemActivated,
      dexemConfig: {
        connectionId: dexemConnectionId,
        emails
      },
      campaignStartDayRange,
      campaignDurationAvertissement,
      minDelayDuration,
      maxDelayDuration,
      minDelayUnit,
      maxDelayUnit,
      features: {
        spendings,
        enabledPmlFront,
        digitaleo,
        activationEnrichedHome,
        magasinViewOnly,
        sendCreationEmailOnCampaignValidation,
        sendCreationEmail,
        copyPal,
        pmlActivationRateBanner,
        shortCampaign,
        longCampaign,
        customBrevoV2ApiKey,
        customBrevoV3ApiKey,
        gettyPrivateKey,
        gettySecretKey,
        maskFees,
        enableDeals,
        budgetSmoothing,
        autoSendInvoice,
        performanceAlert,
        toleranceLimit,
        emailPerformance: emailPerformance.map(el => el.email),
        alertingKpi,
        alertingFrequency,
        allowSellerMail,
        sharedCustomInsightsUsers: selectedUsers.map(el => el._id),
        autoDeleteOldCampaigns,
        whatsappAgent,
        autoCreatePresetByGroup,
        hideFunnelBo,
        duplicateModifyCampaigns,
        campaignSponsoringMeta,
        enabledPmlRepiquage,
        budgetTypeImpressions
      },
      logo,
      darkLogo,
      emailContact,
      cgu,
      dpa,
      legalTerms,
      privacyPolicy,
      domain,
      dataProviders: isEmptyDataProviders
        ? dataProviders
        : flattenDataProviders(dataProviders),
      excludeDataProviders: isEmptyExcludeDataProviders
        ? excludeDataProviders
        : flattenDataProviders(excludeDataProviders),
      modules,
      dataFeedbackConfigNoInvoice,
      platformAccounts: selectedPlatformAccounts.map(serializePlatformAccount),
      platformPages: selectedPlatformPages.map(el => serializePlatformPage(el)),
      groupTaxonomy: [
        shopName,
        ...groupTaxonomy.filter(el => el.level !== 'shop')
      ],
      createCustomers,
      customTheme,
      activablePurchaseOrder,
      activableMedias: selectedActivableMedias,
      geoInterfaces:
        selectedGeoInterfaces.length === geoInterfaces.length + 2 // +2 for isochrony and radius
          ? []
          : selectedGeoInterfaces,
      isLightContent,
      defaultParentShopGroup: defaultParentShopGroup || null,
      defaultProfileKey: defaultProfileKey || null,
      adminUserIds: adminUsers.map(el => el._id) || [],
      defaultFunnelId: defaultFunnelId || null,
      countries: countries || [],
      defaultGeoZone: {
        type: defaultGeoZoneType,
        values: defaultGeoZoneValues
      },
      defaultTargetingTypesByMedia: flattenDefaultTargetingTypesByMedia(
        defaultTargetingTypesByMedia
      ),
      activationRequiredMediaConfigs: formatActivationRequiredMediaConfigs(
        activationRequiredMediaConfigs
      ),
      activableMediaAutomationConfigs: formatActivableMediaAutomationConfigs(
        activableMediaAutomationConfigs
      )
    };

    if (!error) {
      onValidate(datas);
    }
  };

  const generateToken = async () => {
    if (!autoLoginEmail.trim()) {
      setAutoLoginEmailError('Ce champ est obligatoire');
      return;
    }
    const response = await DOMAINCONFIG.generateAutoLoginToken(
      config?.name,
      autoLoginEmail
    );
    if (Object.keys(response).length === 0) {
      setAutoLoginEmailError('Email non existant');
      return;
    }
    setToken(response.jwtToken);
  };

  const getModuleName = key => {
    switch (key) {
      case 'alice':
        return 'Crea';
      case 'crm':
        return 'Insights';
      case 'audiences':
        return 'Data';
      case 'funnel':
        return 'Mesure';
      case 'activation':
        return 'Media';
      case 'pilotage':
        return 'Section pilotage droit mesure';
      default:
        return capitalize(key);
    }
  };

  const fetchUsers = async (search, adminOnly) => {
    const body = { search };
    if (adminOnly) {
      body.roles = ['ADMIN'];
    }
    const found = await USERS.getPaginated(body);
    if (found?.result) {
      setUsers(found.result);
    }
  };

  const pollUsers = (query, adminOnly) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      fetchUsers(query, adminOnly);
    }, 500);
  };

  const loadData = async () => {
    const ids = [
      ...(config?.features?.sharedCustomInsightsUsers || []),
      ...(config?.adminUserIds || [])
    ];
    const [currentUsers] = await Promise.all([
      USERS.getByIds(ids),
      fetchUsers(''),
      fetchUsers('', true),
      fetchFunnels(config?.defaultFunnelId || '')
    ]);

    const initialState = {
      name: config?.name || '',
      spendings: !!config?.features?.spendings,
      digitaleo: !!config?.features?.digitaleo,
      enabledPmlFront: !!config?.features?.enabledPmlFront,
      activationEnrichedHome: !!config?.features?.activationEnrichedHome,
      magasinViewOnly: !!config?.features?.magasinViewOnly,
      copyPal: !!config?.features?.copyPal,
      pmlActivationRateBanner: !!config?.features?.pmlActivationRateBanner,
      sendCreationEmailOnCampaignValidation: !!config?.features
        ?.sendCreationEmailOnCampaignValidation,
      campaignCrmPreset: config?.campaignCrm?.presetId,
      campaignCrmActivated: config?.campaignCrm?.active,
      defaultStripeCustomerActive: config?.defaultStripeCustomer?.active,
      defaultStripeCustomerId: config?.defaultStripeCustomer?.id,
      campaignStartDayRange: config?.campaignStartDayRange ?? 2,
      campaignDurationAvertissement:
        config?.campaignDurationAvertissement || false,
      minDelayDuration: config?.minDelayDuration || 0,
      maxDelayDuration: config?.maxDelayDuration || 0,
      minDelayUnit: config?.minDelayUnit || 'Jour',
      maxDelayUnit: config?.maxDelayUnit || 'Jour',
      activablePurchaseOrder: config?.activablePurchaseOrder || false,
      sendCreationEmail:
        config?.features?.sendCreationEmail === false
          ? config?.features?.sendCreationEmail
          : true,
      maskFees: !!config?.features?.maskFees,
      enableDeals: !!config?.features?.enableDeals,
      dexemActivated: !!config?.dexem,
      dexemEmails: (config?.dexemConfig?.emails || []).map(el => ({
        email: el
      })),
      dexemConnectionId: config?.dexemConfig?.connectionId,
      logo: config?.logo || '',
      domain: config?.domain || '',
      darkLogo: config?.darkLogo || '',
      dataProviders: (config?.dataProviders || []).map(el => ({
        key: el,
        label: el
      })),
      excludeDataProviders: (config?.excludeDataProviders || []).map(el => ({
        key: el,
        label: el
      })),
      emailContact: config?.emailContact || '',
      dataFeedbackConfigNoInvoice: !!config?.dataFeedbackConfigNoInvoice,
      cgu: config?.cgu || '',
      dpa: config?.dpa || '',
      legalTerms: config?.legalTerms || '',
      privacyPolicy: config?.privacyPolicy || '',
      modules: {
        ...formatModule(),
        ...(config?.modules || {})
      },
      selectedPlatformPages:
        config?.platformPages?.map(e => {
          const connector = connectorsConnexions.find(
            el => el.value === e.config.connexionId
          );
          return {
            name: e.name,
            media: e.media,
            connexionId: connector
              ? {
                  value: e.config.connexionId,
                  label: connector?.label
                }
              : undefined,

            externalId: e.config.externalId,
            status: e.status
          };
        }) || [],
      selectedPlatformAccounts:
        config?.platformAccounts?.map(e => {
          const connector = connectorsConnexions.find(
            el => el.value === e.config.connexionId
          );
          const linkConnector = connectorsConnexions.find(
            el => el.value === e.config.linkConnexionId
          );
          return {
            name: e.name,
            model: e.model,
            connexionId: connector
              ? {
                  value: e.config.connexionId,
                  label: connector?.label
                }
              : undefined,
            externalId: e.config.externalId,
            linkConnexionId: linkConnector
              ? {
                  value: e.config.linkConnexionId,
                  label: linkConnector?.label
                }
              : undefined,
            linkExternalId: e.config.linkExternalId,
            status: e.status
          };
        }) || [],
      shopName: config.groupTaxonomy?.find(el => el.level === 'shop') || {
        level: 'shop',
        name: 'Magasin',
        key: 'shop'
      },
      groupTaxonomy: config.groupTaxonomy || [],
      autoLoginEmail: '',
      createCustomers: config?.createCustomers,
      customTheme: config?.customTheme || formatColors(),
      selectedActivableMedias:
        config?.activableMedias || domainConfig?.activableMedias || [],
      selectedGeoInterfaces:
        !config?.geoInterfaces || config?.geoInterfaces?.length === 0
          ? [...geoInterfaces.map(el => el.type), 'isochrony', 'radius']
          : config?.geoInterfaces,
      isLightContent: !!domainConfig?.isLightContent,
      shortCampaign: !!config?.features?.shortCampaign,
      longCampaign: !!config?.features?.longCampaign,
      customBrevoV2ApiKey: config?.features?.customBrevoV2ApiKey || '',
      customBrevoV3ApiKey: config?.features?.customBrevoV3ApiKey || '',
      gettyPrivateKey: config?.features?.gettyPrivateKey || '',
      gettySecretKey: config?.features?.gettySecretKey || '',
      budgetSmoothing: !!config?.features?.budgetSmoothing,
      autoSendInvoice: !!config?.features?.autoSendInvoice,
      performanceAlert: !!config?.features?.performanceAlert,
      allowSellerMail: !!config?.features?.allowSellerMail,
      toleranceLimit: config?.features?.toleranceLimit || [10],
      emailPerformance: (config?.features?.emailPerformance || []).map(el => ({
        email: el
      })),
      alertingKpi: config?.features?.alertingKpi || 'budget',
      alertingFrequency: config?.features?.alertingFrequency || 'daily',
      selectedUsers:
        config?.features?.sharedCustomInsightsUsers?.map(element => {
          const user = currentUsers.find(el => el._id === element);
          return {
            email: user ? user.email : '',
            _id: element
          };
        }) || [],
      defaultParentShopGroup: config?.defaultParentShopGroup,
      defaultProfileKey: config?.defaultProfileKey,
      autoDeleteOldCampaigns:
        config?.features?.autoDeleteOldCampaigns ??
        config?.autoDeleteOldCampaigns ??
        false,
      whatsappAgent: !!config?.features?.whatsappAgent,
      autoCreatePresetByGroup: !!config?.features?.autoCreatePresetByGroup,
      adminUsers:
        config?.adminUserIds?.map(el =>
          formatListFieldUser(currentUsers.find(user => user._id === el))
        ) || [],
      countries: config?.countries || [],
      defaultFunnelId: config?.defaultFunnelId,
      defaultGeoZoneType: config?.defaultGeoZone?.type || null,
      defaultGeoZoneValues: config?.defaultGeoZone?.values || null,
      defaultTargetingTypesByMedia: groupDefaultTargetingTypesByMedia(
        config?.defaultTargetingTypesByMedia || []
      ),
      hideFunnelBo: !!config?.features?.hideFunnelBo,
      duplicateModifyCampaigns: !!config?.features?.duplicateModifyCampaigns,
      campaignSponsoringMeta: !!config?.features?.campaignSponsoringMeta,
      enabledPmlRepiquage: !!config?.features?.enabledPmlRepiquage,
      activationRequiredMediaConfigs: mapActivationRequiredMediaConfigsFromConfig(
        config?.activationRequiredMediaConfigs || []
      ),
      activableMediaAutomationConfigs: mapActivableMediaAutomationConfigsFromConfig(
        config?.activableMediaAutomationConfigs || [],
        activableMedias
      ),
      budgetTypeImpressions: !!config?.features?.budgetTypeImpressions
    };

    setState(initialState);
    setLoaded(true);
  };

  useEffect(() => {
    if (!loaded) {
      loadData();
    }
    // eslint-disable-next-line
  }, []);

  // if (!loaded) {
  //   return <></>;
  // }

  const formTitle =
    title || `Configuration du Domaine : ${name || config?.name || domain}`;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 'clamp(83rem, 68vw, 106rem)',
        minWidth: 0,
        margin: 0,
        boxSizing: 'border-box',
        overflowX: 'clip'
      }}
    >
      <DomainPageActionBar
        title={formTitle}
        saveLabel={saveLabel || `${actionName} le domaine`}
        createLabel={createLabel}
        onCancel={onClose}
        onCreate={onCreate}
        onSave={doAction}
      />
      {!loaded ? (
        <Loading loading />
      ) : (
        <Container
          style={{
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            padding: '0 0 1.5rem'
          }}
        >
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Général" xs={3}>
                <Row spacing={0} justify="space-between">
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={10} justify="flex-start">
                        <TextField
                          title="Nom du domain"
                          value={name}
                          error={nameError}
                          placeholder="nom du domain"
                          onChange={handleChange('name')}
                        />
                      </Item>
                    </Row>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <Switch
                          title="Masquer les frais data dans le funnel"
                          checked={dataFeedbackConfigNoInvoice}
                          onChange={handleChange('dataFeedbackConfigNoInvoice')}
                          options={['Afficher', 'Masquer']}
                        />
                      </Item>
                    </Row>
                  </Item>
                </Row>
                <Row spacing={0}>
                  <Item justify="flex-start">
                    <Row spacing={0} justify="space-between">
                      <Item xs={5} justify="flex-start">
                        <Select
                          title="Délai pour lancement de campagnes"
                          small
                          value={campaignStartDayRange}
                          options={campaignStartDayRanges}
                          tooltip="Par défaut 2 jours ouvrés sont nécessaires entre la validation de campagne et le lancement effectif.
                          Si une valeur (en jours) est indiquée alors elle prendra le dessus sur les 2 jours par défaut."
                          onChange={value => {
                            handleChange('campaignStartDayRange')(value);
                          }}
                        />
                      </Item>
                    </Row>
                  </Item>
                </Row>
                <Row spacing={0} alignItems="center">
                  <Item justify="flex-start">
                    <Item xs={3} justify="flex-start">
                      <Switch
                        title="Avertissement sur la durée"
                        checked={campaignDurationAvertissement}
                        onChange={handleChange('campaignDurationAvertissement')}
                        options={['', 'Activer']}
                      />
                    </Item>
                    {campaignDurationAvertissement && (
                      <>
                        <Item
                          xs={2}
                          justify="flex-start"
                          sx={{ marginRight: 1 }}
                        >
                          <TextField
                            title="Délai minimum"
                            value={minDelayDuration}
                            placeholder="0"
                            onChange={value => {
                              handleChange('minDelayDuration')(value);
                            }}
                          />
                        </Item>
                        <Item
                          xs={1.2}
                          justify="flex-start"
                          sx={{ marginRight: 4 }}
                        >
                          <Select
                            title="Unité"
                            small
                            value={minDelayUnit}
                            options={delayDurationRanges}
                            onChange={value => {
                              handleChange('minDelayUnit')(value);
                            }}
                          />
                        </Item>
                        <Item
                          xs={2}
                          justify="flex-start"
                          sx={{ marginRight: 1 }}
                        >
                          <TextField
                            title="Délai maximum"
                            value={maxDelayDuration}
                            placeholder="0"
                            onChange={value => {
                              handleChange('maxDelayDuration')(value);
                            }}
                          />
                        </Item>
                        <Item xs={1.2} justify="flex-start">
                          <Select
                            title="Unité"
                            small
                            value={maxDelayUnit}
                            options={delayDurationRanges}
                            onChange={value => {
                              handleChange('maxDelayUnit')(value);
                            }}
                          />
                        </Item>
                      </>
                    )}
                  </Item>
                </Row>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Sources de données" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item xs={6} justify="flex-start">
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <Container>
                            <Item justify="flex-start">
                              <MultipleSelect
                                title="Inclure"
                                small
                                placeHolder="Toutes les sources de données"
                                options={dataProvidersOptions}
                                selected={dataProviders.map(el => el.key)}
                                onChange={value => {
                                  handleChange('dataProviders')(value);
                                }}
                              />
                            </Item>
                          </Container>
                        </Item>
                      </Row>
                    </Item>
                    <Item xs={6} justify="flex-start">
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <Container>
                            <Item justify="flex-start">
                              <MultipleSelect
                                title="Exclure"
                                small
                                placeHolder="Aucune source de données"
                                options={dataProvidersOptions}
                                selected={excludeDataProviders.map(
                                  el => el.key
                                )}
                                onChange={value => {
                                  handleChange('excludeDataProviders')(value);
                                }}
                              />
                            </Item>
                          </Container>
                        </Item>
                      </Row>
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Domaine international" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item justify="flex-start" xs={12}>
                      <ListField
                        variant="customFields"
                        values={countries}
                        onChange={handleChange('countries')}
                        actionTxt="Ajouter un pays"
                        deleteAction
                        inputs={['Select', 'Switch']}
                        labels={['Pays', 'Utilisation de donnée']}
                        options={['key', 'skipData']}
                        tooltips={[
                          '',
                          'Si vous ne souhaitez pas utiliser la base de donnée Ermes, activez la fonctionnalité.'
                        ]}
                        fieldsFormat={[
                          'countries',
                          ['Data Ermes', 'Pas de data Ermes']
                        ]}
                        isRequireds={[true, false]}
                      />
                    </Item>
                    {countries.some(el => el.skipData) && (
                      <Row padding="0px">
                        <Item xs={1} style={{ padding: '0px' }}>
                          <InfoIcon
                            style={{
                              color: themeColors.themeV2.yellow
                            }}
                          />
                        </Item>
                        <Item xs={11} style={{ padding: '0px' }}>
                          <Text>
                            L’un de vos pays n’utilisant pas la data Ermes, nous
                            masquerons toute notion de nombre de profils et de
                            répétition, à moins que le pays précisé dans le
                            parcours utilise la data Ermes.
                          </Text>
                        </Item>
                      </Row>
                    )}
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Hiéarchie des groupements" xs={3}>
                <Row spacing={0} justify="space-between">
                  <ListField
                    values={groupTaxonomy.filter(el => el.level !== 'shop')}
                    onChange={handleChangeGroupTaxonomyAdd}
                    actionTxt="Ajouter un niveau"
                    deleteAction
                    variant="customFields"
                    inputs={['TextField', 'TextField']}
                    labels={['Niveau', 'Nom']}
                    options={['level', 'name']}
                    fieldsFormat={['numberDec']}
                  />
                </Row>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Nom des magasins" xs={3}>
                <Row spacing={0} justify="space-between">
                  <Item xs={5} justify="flex-start">
                    <TextField
                      title="Nom des magasins"
                      value={shopName?.name}
                      error={shopNameError}
                      placeholder="Choisissez le nom de vos magasins"
                      onChange={handleChange('shopName')}
                    />
                  </Item>
                </Row>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item xs={12} justify="flex-start">
              <FormSection title="Fonctionnalités" xs={12}>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Upload des dépenses médias offline</Text>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={spendings}
                      color={themeColors.primary}
                      onChange={handleChange('spendings')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Campagnes PAL dans activation</Text>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={activationEnrichedHome}
                      color={themeColors.primary}
                      onChange={handleChange('activationEnrichedHome')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={5} justify="flex-start" sx={{ marginBottom: 3 }}>
                    <AutocompleteTextField
                      title="Groupement de magasin parent par défaut"
                      placeholder="Choisissez un groupement de magasin"
                      tooltip="Le groupe parent par défaut est utilisé lors de la création de réseau via le front. Vous pouvez sélectionner seulement un groupe de magasin ne contenant aucun magasin."
                      options={parsedShopGroups}
                      small
                      formatLabel={option => `${option.label} (${option.id})`}
                      value={parsedShopGroups.find(
                        el => el.id === defaultParentShopGroup
                      )}
                      onChange={value =>
                        handleChange('defaultParentShopGroup')(value?.id)
                      }
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Envoi d&apos;email à la création utilisateur</Text>
                    <HelpTooltip
                      title="Si vous décochez cette option, alors l'utilisateur ne recevra pas d'email lors de sa création pour setuper son mot de passe sur ce domaine et ne pourra pas le reset. Vous pourrez toujours changer ce choix lors de la création d'utilisateur."
                      style={{
                        paddingLeft: '10px'
                      }}
                    />
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={sendCreationEmail}
                      color={themeColors.primary}
                      onChange={handleChange('sendCreationEmail')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>
                      Création de preset à la création d’une organisation
                    </Text>
                    <HelpTooltip
                      title="Cette fonctionnalité permet de créer automatiquement un preset d’activation à chaque création d’organisation."
                      style={{
                        paddingLeft: '10px'
                      }}
                    />
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={autoCreatePresetByGroup}
                      color={themeColors.primary}
                      onChange={handleChange('autoCreatePresetByGroup')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>
                      Création d&apos;utilisateur à la première campagne
                    </Text>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={sendCreationEmailOnCampaignValidation}
                      color={themeColors.primary}
                      onChange={handleChange(
                        'sendCreationEmailOnCampaignValidation'
                      )}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Activer l&apos;autologin</Text>
                    <HelpTooltip
                      title="L'autologin demande du développement côté équipe plateforme. Ne pas l'activer si celui-ci n'a pas été fait en amont."
                      style={{
                        paddingLeft: '10px'
                      }}
                    />
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      disabled={autoLogin === undefined}
                      checked={autoLogin}
                      color={themeColors.primary}
                      onChange={handleChange('autoLogin')}
                    />
                  </Item>
                </Row>
                {autoLogin && (
                  <Row
                    style={{
                      alignItems: 'baseline'
                    }}
                    spacing={0}
                    justify="flex-start"
                  >
                    <Item
                      xs={3}
                      style={{
                        marginRight: '10px'
                      }}
                    >
                      <TextField
                        value={autoLoginEmail}
                        placeholder="Email utilisateur"
                        onChange={handleChange('autoLoginEmail')}
                        error={autoLoginEmailError.length > 0}
                        errorText={autoLoginEmailError}
                        helperText={
                          <FormHelperText
                            style={{
                              maxWidth: '200px',
                              overflow: 'hidden',
                              lineBreak: 'anywhere'
                            }}
                          >
                            {token && `Token généré: ${token}`}
                          </FormHelperText>
                        }
                      />
                    </Item>
                    <Item xs={3}>
                      <Button
                        variant="contained"
                        color={themeColors.backoffice.global}
                        size="medium"
                        widthSize="medium"
                        onClick={generateToken}
                      >
                        Générer un token
                      </Button>
                    </Item>
                  </Row>
                )}
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Magasin en lecture seule</Text>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={magasinViewOnly}
                      color={themeColors.primary}
                      onChange={handleChange('magasinViewOnly')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Setup PML rôle standard</Text>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={enabledPmlFront}
                      color={themeColors.primary}
                      onChange={handleChange('enabledPmlFront')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Dupliquer les campagnes PAL au sein du réseau</Text>
                    <HelpTooltip
                      title="Si activé, toutes les campagnes PAL d'un réseau seront présentes dans tous les shops de ce réseau"
                      style={{
                        paddingLeft: '10px'
                      }}
                    />
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={copyPal}
                      color={themeColors.primary}
                      onChange={handleChange('copyPal')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>
                      Bandeau taux d&apos;activation dashboard réseau PML
                    </Text>
                    <HelpTooltip
                      title="Si actif, un bandeau résumant le taux d'activation des magasins qui composent le groupe s'affiche."
                      style={{
                        paddingLeft: '10px'
                      }}
                    />
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={pmlActivationRateBanner}
                      color={themeColors.primary}
                      onChange={handleChange('pmlActivationRateBanner')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Connexion à Digitaléo</Text>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={digitaleo}
                      color={themeColors.primary}
                      onChange={handleChange('digitaleo')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Lissage de budget</Text>
                    <HelpTooltip
                      title="Lorsque cette fonctionnalité est activée, les frais techniques et frais datas seront masqués. Ces montants seront répartis sur les budgets médias."
                      style={{
                        paddingLeft: '10px'
                      }}
                    />
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={maskFees}
                      color={themeColors.primary}
                      onChange={handleChange('maskFees')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>KPI funnel : Budget lissé par jour</Text>
                    <HelpTooltip
                      title="Si actif, on a un KPI funnel Budget à dépenser et Budget dépensé qui est (budget à dépenser / nombre de jours de la campagne) * nombre de jours passés"
                      style={{
                        paddingLeft: '10px'
                      }}
                    />
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={budgetSmoothing}
                      color={themeColors.primary}
                      onChange={handleChange('budgetSmoothing')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Numéros de tracking Dexem</Text>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={dexemActivated}
                      color={themeColors.primary}
                      onChange={handleChange('dexemActivated')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Alerting performance campagnes</Text>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={performanceAlert}
                      color={themeColors.primary}
                      onChange={handleChange('performanceAlert')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Envoi d&apos;email au télévendeur</Text>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={allowSellerMail}
                      color={themeColors.primary}
                      onChange={handleChange('allowSellerMail')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Profil par défault BO simplifié</Text>
                  </Item>
                  <HelpTooltip
                    title="Ce profil sera appliqué pour toute création d’utilisateur depuis le BO simplifié."
                    style={{
                      paddingLeft: '10px'
                    }}
                  />
                  <Item xs={6} justify="flex-start">
                    <Item xs={6}>
                      <CustomSelect
                        small
                        searchBar
                        hideNone
                        selected={defaultProfileKey}
                        options={profileKeys}
                        onChange={value => {
                          handleChange('defaultProfileKey')(value);
                        }}
                      />
                    </Item>
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Agent Ermes what’s app</Text>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={whatsappAgent}
                      color={themeColors.primary}
                      onChange={handleChange('whatsappAgent')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Utiliser les Deals Leboncoin</Text>
                    <HelpTooltip
                      title="Lorsque cette fonctionnalité est activée, les Deals Leboncoin sont utilisés lors des setups automatiques DV360."
                      style={{
                        paddingLeft: '10px'
                      }}
                    />
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={enableDeals}
                      color={themeColors.primary}
                      onChange={handleChange('enableDeals')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Cacher section Funnel dans le BO</Text>
                    <HelpTooltip
                      title="Lorsque cette fonctionnalité est activée, la section Funnel du BO ne sera plus accessible sur ce domain"
                      style={{
                        paddingLeft: '10px'
                      }}
                    />
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={hideFunnelBo}
                      color={themeColors.primary}
                      onChange={handleChange('hideFunnelBo')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Duplication/modification de campagne</Text>
                    <HelpTooltip
                      title="L’activation de ce droit permet de rendre la fonctionnalité disponible pour l’ensemble du domaine."
                      style={{
                        paddingLeft: '10px'
                      }}
                    />
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={duplicateModifyCampaigns}
                      color={themeColors.primary}
                      onChange={handleChange('duplicateModifyCampaigns')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Import campagnes et créas repiquées</Text>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={enabledPmlRepiquage}
                      color={themeColors.primary}
                      onChange={handleChange('enabledPmlRepiquage')}
                    />
                  </Item>
                </Row>
              </FormSection>
            </Item>
          </Row>
          {performanceAlert && (
            <Row spacing={1}>
              <Item justify="flex-start">
                <FormSection
                  title="Configuration de l'alerting performance"
                  xs={3}
                >
                  <Container>
                    <Row spacing={1} justify="flex-start">
                      <Item justify="flex-start">
                        <Switch
                          title="Type de suivi"
                          required
                          checked={alertingKpi === 'kpi1'}
                          onChange={toggleTrackingType}
                          options={['Budgétaire', 'Impressions']}
                        />
                      </Item>
                      <Item xs={6} justify="flex-start">
                        <Switch
                          title="Périodicité"
                          required
                          checked={alertingFrequency === 'weekly'}
                          onChange={toggleFrequency}
                          options={['Quotidienne', 'Hebdomadaire']}
                        />
                      </Item>
                    </Row>
                    <Row spacing={1}>
                      <Item xs={12} justify="flex-start">
                        <Row spacing={0}>
                          <Item justify="flex-start">
                            <Text>Seuils de tolérance (%)</Text>
                            <Tooltip title="Renseignez les seuils dans l'ordre, du plus petit, au plus grand. Ils apparaitront alors dans l'ordre décroissant.">
                              <HelpIcon
                                style={{
                                  color: themeColors.light,
                                  width: '20px'
                                }}
                              />
                            </Tooltip>
                          </Item>
                        </Row>

                        <ListField
                          variant="customFields"
                          labels={['Seuil de tolérance (%)']}
                          values={
                            Array.isArray(toleranceLimit)
                              ? toleranceLimit.map(e => ({ name: `${e}` }))
                              : [{ name: `${toleranceLimit}` }]
                          }
                          onChange={value => {
                            const newValues = value.map(el =>
                              parseInt(el.name, 10)
                            );
                            handleChange('toleranceLimit')(
                              newValues.length === 1 ? newValues[0] : newValues
                            );
                          }}
                          actionTxt="Ajouter un seuil"
                          deleteAction
                          inputs={['TextField']}
                          options={['name']}
                          isRequireds={[true]}
                          placeholder="Choisissez un seuil"
                          bgColor={themeColors.primary}
                          fieldsFormat={['numberDec']}
                        />
                      </Item>
                    </Row>
                    <Row spacing={1}>
                      <Item xs={12} justify="flex-start">
                        <Text>Emails d&apos;alerte performance</Text>
                        <ListField
                          variant="customFields"
                          labels={['Adresse email']}
                          values={emailPerformance}
                          onChange={value =>
                            handleChange('emailPerformance')(value)
                          }
                          actionTxt="Ajouter un email"
                          deleteAction
                          inputs={['TextField']}
                          options={['email']}
                          isRequireds={[true]}
                          placeholder="Saisissez une adresse email"
                          bgColor={themeColors.primary}
                        />
                      </Item>
                    </Row>
                  </Container>
                </FormSection>
              </Item>
            </Row>
          )}
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Personnalisation" xs={3}>
                <Row spacing={0} justify="flex-start">
                  <Item xs={12} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={4} justify="flex-start">
                        <Text fontWeight={700}>Titre</Text>
                      </Item>
                      <Item xs={2} justify="center">
                        <Text fontWeight={700}>Actif</Text>
                      </Item>
                      <Item xs={4} justify="center">
                        <Text fontWeight={700}>Couleur</Text>
                      </Item>
                    </Row>
                    {Object.keys(defaultModule).map(key => {
                      const { colorKey } = defaultModule[key];
                      return (
                        <React.Fragment key={key}>
                          <Row spacing={0} justify="flex-start">
                            <Item xs={4} justify="flex-start">
                              <Text>{getModuleName(key)}</Text>
                            </Item>
                            <Item xs={2}>
                              <Switch
                                checked={modules[key]}
                                color={themeColors.primary}
                                onChange={checked => {
                                  handleModulesChange(key, checked);
                                  if (key === 'alice' && checked) {
                                    handleChange('isLightContent')(!checked);
                                  }
                                }}
                              />
                            </Item>
                            <Item xs={4} justify="center">
                              {colorKey && (
                                <ColorPicker
                                  value={customTheme[colorKey]}
                                  onChange={handleChangeColor(colorKey)}
                                  iconView
                                />
                              )}
                            </Item>
                          </Row>
                          {key === 'alice' && !modules[key] && (
                            <Row spacing={0} justify="flex-start">
                              <Item xs={4} justify="flex-start">
                                <Text>Crea (Editeur only)</Text>
                              </Item>
                              <Item xs={2}>
                                <Switch
                                  checked={isLightContent}
                                  color={themeColors.primary}
                                  onChange={checked => {
                                    handleChange('isLightContent')(checked);
                                  }}
                                />
                              </Item>
                            </Row>
                          )}
                        </React.Fragment>
                      );
                    })}
                    <Row
                      spacing={0}
                      justify="flex-start"
                      style={{ height: '37px' }}
                    >
                      <Item xs={4} justify="flex-start">
                        <Text>Header</Text>
                      </Item>
                      <Item xs={2} />
                      <Item xs={4} justify="center">
                        <ColorPicker
                          value={customTheme.primary_header}
                          onChange={handleChangeColor('primary_header')}
                          iconView
                        />
                      </Item>
                    </Row>
                    <Row
                      spacing={0}
                      justify="flex-start"
                      style={{ height: '37px' }}
                    >
                      <Item xs={4} justify="flex-start">
                        <Text>Ecriture</Text>
                      </Item>
                      <Item xs={2} />
                      <Item xs={4} justify="center">
                        <ColorPicker
                          value={customTheme.txtHeaderColor}
                          onChange={handleChangeColor('txtHeaderColor')}
                          iconView
                        />
                      </Item>
                    </Row>
                    <Row
                      spacing={0}
                      justify="flex-start"
                      style={{ height: '37px' }}
                    >
                      <Item xs={4} justify="flex-start" />
                      <Item xs={2} />
                      <Item xs={4} justify="center">
                        <Button
                          color={themeColors.themeV2.black}
                          onClick={() =>
                            handleChange('customTheme')(formatColors())
                          }
                        >
                          <ClearIcon />
                          &nbsp;Reset
                        </Button>
                      </Item>
                    </Row>
                  </Item>
                </Row>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Activation" xs={3}>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Item xs={11}>
                      <MultipleSelect
                        small
                        required
                        selectAll
                        options={activableMedias.map(el => {
                          return { label: el.name, key: el.key };
                        })}
                        selected={selectedActivableMedias}
                        onChange={value => {
                          handleChange('selectedActivableMedias')(
                            value.map(el => el.key)
                          );
                        }}
                        title="Médias activables"
                      />
                    </Item>
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Activer campagne CRM</Text>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={campaignCrmActivated}
                      color={themeColors.primary}
                      onChange={handleChange('campaignCrmActivated')}
                    />
                  </Item>
                </Row>
                {campaignCrmActivated && (
                  <Row spacing={0} justify="flex-start">
                    <Item xs={5} justify="flex-start" sx={{ marginBottom: 3 }}>
                      <AutocompleteTextField
                        title="Sélectionner un preset CRM"
                        placeholder="Choisissez un preset CRM"
                        options={preset.filter(
                          el => el?.domain === config?.domain
                        )}
                        formatLabel={option => {
                          return `${option.name} (${option._id})`;
                        }}
                        small
                        value={preset.find(el => el._id === campaignCrmPreset)}
                        onChange={handleChange('campaignCrmPreset')}
                        error={campaignCrmPresetError}
                      />
                    </Item>
                  </Row>
                )}
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Campagnes PAL dans activation</Text>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={activationEnrichedHome}
                      color={themeColors.primary}
                      onChange={handleChange('activationEnrichedHome')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Suppression auto des campagne brouillons</Text>
                    <HelpTooltip
                      title="Si activée, cette fonctionnalité supprime les campagnes medias / CRM / PAL non fils rouges si leur date de fin dépasse 30 jours dans le passé."
                      style={{
                        paddingLeft: '10px'
                      }}
                    />
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={autoDeleteOldCampaigns}
                      color={themeColors.primary}
                      onChange={handleChange('autoDeleteOldCampaigns')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Campagnes sponsoring meta</Text>
                    <HelpTooltip
                      title="Ouvrir la possibilité de faire des campagnes de boost de posts existants sur facebook et instagram."
                      style={{
                        paddingLeft: '10px'
                      }}
                    />
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={campaignSponsoringMeta}
                      color={themeColors.primary}
                      onChange={handleChange('campaignSponsoringMeta')}
                    />
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Text>Mode d’achat DV360</Text>
                    <HelpTooltip
                      title="Activé = Impressions / Désactivé = Budget"
                      style={{
                        paddingLeft: '10px'
                      }}
                    />
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      checked={budgetTypeImpressions}
                      color={themeColors.primary}
                      onChange={handleChange('budgetTypeImpressions')}
                    />
                  </Item>
                </Row>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection
                title="Compte pub requis activation / emailing"
                xs={3}
              >
                <Container>
                  {activationRequiredMediaConfigs.length > 0 && (
                    <Row spacing={0} justify="space-between">
                      <Item
                        xs={3}
                        justify="flex-start"
                        style={{
                          paddingLeft: '20px'
                        }}
                      >
                        <Text bold>Média</Text>
                      </Item>
                      <Item
                        xs={4}
                        justify="flex-start"
                        style={{ maxWidth: '100%' }}
                      >
                        <Row
                          spacing={0}
                          justify="flex-start"
                          alignItems="center"
                          style={{ flexWrap: 'nowrap' }}
                        >
                          <Item justify="flex-start">
                            <Text bold noWrap>
                              Compte pub/page requis activation
                            </Text>
                          </Item>
                          <Item justify="flex-start">
                            <HelpTooltip
                              title="Combiné au droit du profil une section apparaît pour renseigner les champs dans activation"
                              style={{
                                paddingLeft: '10px'
                              }}
                            />
                          </Item>
                        </Row>
                      </Item>
                      <Item
                        xs={4}
                        justify="flex-start"
                        style={{ maxWidth: '100%' }}
                      >
                        <Row
                          spacing={0}
                          justify="flex-start"
                          alignItems="center"
                          style={{ flexWrap: 'nowrap' }}
                        >
                          <Item justify="flex-start">
                            <Text bold noWrap>
                              Email tuto ajout compte
                            </Text>
                          </Item>
                          <Item justify="flex-start">
                            <HelpTooltip
                              title="Un email de tutoriel est envoyé au passage de la campagne à prête ou validée pour ajouter le compte pub."
                              style={{
                                paddingLeft: '10px'
                              }}
                            />
                          </Item>
                        </Row>
                      </Item>
                    </Row>
                  )}
                  <Row spacing={0} justify="flex-start">
                    <Item justify="flex-start" xs={11}>
                      <ListField
                        values={activationRequiredMediaConfigs}
                        onChange={handleChange(
                          'activationRequiredMediaConfigs'
                        )}
                        actionTxt="Ajouter un média"
                        deleteAction
                        variant="customFields"
                        valueUnique
                        labels={[
                          'Média',
                          'Compte pub/page requis',
                          'Email tuto ajout compte'
                        ]}
                        options={['media', 'requiredFields', 'tuto']}
                        inputs={['Select', 'MultiSelect', 'Switch']}
                        fieldsFormat={[
                          getActivationRequiredMediaOptions(),
                          ACTIVATION_REQUIRED_FIELD_OPTIONS,
                          ['Désactivé', 'Activé']
                        ]}
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Setup auto direct" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item xs={11}>
                      <Text>
                        Configurez, par média, l’éligibilité au setup
                        automatique sans intervention BO (« Setup auto direct »)
                        et le statut par défaut des campagnes créées (« Campagne
                        active par défaut »).
                      </Text>
                    </Item>
                  </Row>
                  {activableMediaAutomationConfigs.length > 0 && (
                    <Row
                      spacing={0}
                      justify="flex-start"
                      style={{ paddingTop: '10px' }}
                    >
                      <Item justify="flex-start" xs={11}>
                        <Container justify="flex-start">
                          <Row spacing={2} justify="flex-start">
                            <Item xs={12}>
                              <Container>
                                <Row spacing={0}>
                                  <Item
                                    xs
                                    justify="flex-start"
                                    style={{ padding: '5px' }}
                                  >
                                    <Text bold>Médias</Text>
                                  </Item>
                                  <Item
                                    xs
                                    justify="flex-start"
                                    style={{ padding: '5px' }}
                                  >
                                    <Text bold noWrap>
                                      Setup auto direct
                                    </Text>
                                  </Item>
                                  <Item
                                    xs
                                    justify="flex-start"
                                    style={{ padding: '5px' }}
                                  >
                                    <Text bold noWrap>
                                      Campagne active par défaut
                                    </Text>
                                  </Item>
                                  <IconButton style={{ visibility: 'hidden' }}>
                                    <DeleteIcon />
                                  </IconButton>
                                  <IconButton style={{ visibility: 'hidden' }}>
                                    <DeleteIcon />
                                  </IconButton>
                                </Row>
                              </Container>
                            </Item>
                          </Row>
                        </Container>
                      </Item>
                    </Row>
                  )}
                  <Row spacing={0} justify="flex-start">
                    <Item justify="flex-start" xs={11}>
                      <ListField
                        values={activableMediaAutomationConfigs}
                        onChange={handleChange(
                          'activableMediaAutomationConfigs'
                        )}
                        actionTxt="Ajouter une configuration"
                        deleteAction
                        variant="customFields"
                        labels={[
                          'Médias',
                          'Setup auto direct',
                          'Campagne active par défaut'
                        ]}
                        options={[
                          'activableMedias',
                          'setupAuto',
                          'activeByDefault'
                        ]}
                        inputs={['MultiSelect', 'Switch', 'Switch']}
                        fieldsFormat={[
                          getActivableMediaAutomationOptions(),
                          ['Désactivé', 'Activé'],
                          ['Désactivé', 'Activé']
                        ]}
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Ciblages géographiques" xs={3}>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6}>
                    <Item xs={11}>
                      <MultipleSelect
                        small
                        required
                        selectAll
                        options={[
                          ...geoInterfaces.map(({ name: geoName, type }) => ({
                            label: geoName.fr,
                            key: type
                          })),
                          { label: 'Isochronie', key: 'isochrony' },
                          { label: 'Rayon', key: 'radius' }
                        ]}
                        selected={selectedGeoInterfaces}
                        onChange={value => {
                          handleChange('selectedGeoInterfaces')(
                            value.map(el => el.key)
                          );
                        }}
                      />
                    </Item>
                  </Item>
                </Row>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item xs={12} justify="flex-start">
              <FormSection title="Logo" xs={12}>
                <Row spacing={0} justify="flex-start">
                  <Item xs={12} justify="flex-start">
                    <Row spacing={1}>
                      <Item xs={12} justify="flex-start">
                        <Text>Logo</Text>
                      </Item>
                      <Item xs={12} justify="flex-start">
                        <Row
                          spacing={0}
                          justify="flex-start"
                          alignItems="center"
                        >
                          <Item xs={5} justify="flex-start">
                            <input
                              type="file"
                              ref={hiddenFileLogoInput}
                              onChange={handleLogoChange('logo')}
                              style={{
                                display: 'none'
                              }}
                            />
                            {logo ? (
                              <Image
                                src={logo}
                                style={{
                                  height: '40px'
                                }}
                              />
                            ) : (
                              <TextField
                                small
                                label="sélectionner une image"
                                disabled
                                style={{
                                  width: '100%'
                                }}
                              />
                            )}
                          </Item>
                          <Item
                            xs={2}
                            justify="flex-start"
                            style={{
                              padding: '0px 16px'
                            }}
                          >
                            {logo && (
                              <IconButton
                                onClick={() => handleChange('logo')('')}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                            <Button
                              variant="contained"
                              size="medium"
                              widthSize="small"
                              onClick={() => {
                                hiddenFileLogoInput.current.click();
                              }}
                            >
                              {logo ? 'Modifier' : 'Parcourir'}
                            </Button>
                          </Item>
                        </Row>
                      </Item>
                    </Row>
                    <Row spacing={1}>
                      <Item xs={12} justify="flex-start">
                        <Text>Logo sombre</Text>
                      </Item>
                      <Item xs={12} justify="flex-start">
                        <Row
                          spacing={0}
                          justify="flex-start"
                          alignItems="center"
                        >
                          <Item xs={5} justify="flex-start">
                            <input
                              type="file"
                              ref={hiddenFileDarkLogoInput}
                              onChange={handleLogoChange('darkLogo')}
                              style={{
                                display: 'none'
                              }}
                            />
                            {darkLogo ? (
                              <Image
                                src={darkLogo}
                                style={{
                                  height: '40px'
                                }}
                              />
                            ) : (
                              <TextField
                                small
                                label="sélectionner une image"
                                disabled
                                style={{
                                  width: '100%'
                                }}
                              />
                            )}
                          </Item>
                          <Item
                            xs={2}
                            justify="flex-start"
                            style={{
                              padding: '0px 16px'
                            }}
                          >
                            {darkLogo && (
                              <IconButton
                                onClick={() => handleChange('darkLogo')('')}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                            <Button
                              variant="contained"
                              size="medium"
                              widthSize="small"
                              onClick={() =>
                                hiddenFileDarkLogoInput.current.click()
                              }
                            >
                              {darkLogo ? 'Modifier' : 'Parcourir'}
                            </Button>
                          </Item>
                        </Row>
                      </Item>
                    </Row>
                  </Item>
                </Row>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Contact" xs={3}>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <TextField
                          title="Adresse email de contact"
                          value={emailContact}
                          placeholder="contact@ermes.ai"
                          onChange={handleChange('emailContact')}
                        />
                      </Item>
                    </Row>
                  </Item>
                </Row>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Confidentialité" xs={3}>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <TextField
                          title="Lien de CGU"
                          value={cgu}
                          error={cguError}
                          errorText="Lien incorrect"
                          placeholder="https://..."
                          onChange={handleChange('cgu')}
                        />
                      </Item>
                    </Row>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <TextField
                          title="Lien de DPA"
                          value={dpa}
                          error={dpaError}
                          errorText="Lien incorrect"
                          placeholder="https://..."
                          onChange={handleChange('dpa')}
                        />
                      </Item>
                    </Row>
                  </Item>
                </Row>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <TextField
                          title="Lien des mentions légales"
                          value={legalTerms}
                          error={legalTermsError}
                          errorText="Lien incorrect"
                          placeholder="https://..."
                          onChange={handleChange('legalTerms')}
                        />
                      </Item>
                    </Row>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <TextField
                          title="Lien de la politique de confidentialité"
                          value={privacyPolicy}
                          error={privacyPolicyError}
                          errorText="Lien incorrect"
                          placeholder="https://..."
                          onChange={handleChange('privacyPolicy')}
                        />
                      </Item>
                    </Row>
                  </Item>
                </Row>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Facturation" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item xs={6} justify="flex-start">
                      <Text>Comptes de paiement pour tous</Text>
                      <HelpTooltip
                        title="Des comptes de paiement seront automatiquement créés pour toute organisation actuelle et future du domaine."
                        style={{
                          paddingLeft: '10px'
                        }}
                      />
                    </Item>
                    <Item xs={6} justify="flex-start">
                      <Switch
                        disabled={createCustomers === undefined}
                        checked={createCustomers}
                        color={themeColors.primary}
                        onChange={handleChange('createCustomers')}
                      />
                    </Item>
                  </Row>
                  <Row spacing={0} justify="flex-start">
                    <Item xs={6} justify="flex-start">
                      <Text>Clients - bons de commande</Text>
                      <HelpTooltip
                        title="Les comptes de paiement créés et existants suivront par défaut le fonctionnement de facturation par bon de commande. Vous pourrez modifier cette valeur indépendamment pour chaque client."
                        style={{
                          paddingLeft: '10px'
                        }}
                      />
                    </Item>
                    <Item xs={6} justify="flex-start">
                      <Switch
                        disabled={activablePurchaseOrder === undefined}
                        checked={activablePurchaseOrder}
                        color={themeColors.primary}
                        onChange={handleChange('activablePurchaseOrder')}
                      />
                    </Item>
                  </Row>
                  <Row spacing={0} justify="flex-start">
                    <Item xs={6} justify="flex-start">
                      <Text>Envoi automatique de facture</Text>
                      <HelpTooltip
                        title="Changer la valeur à ce niveau, modifiera le statut de tous les comptes clients existants. Vous pouvez toujours changer cette valeur pour chaque client dans la fiche client du back-office."
                        style={{
                          paddingLeft: '10px'
                        }}
                      />
                    </Item>
                    <Item xs={6} justify="flex-start">
                      <Switch
                        checked={autoSendInvoice}
                        color={themeColors.primary}
                        onChange={handleChange('autoSendInvoice')}
                      />
                    </Item>
                  </Row>
                  <Row spacing={0} justify="flex-start">
                    <Item xs={6} justify="flex-start">
                      <Text>Compte de facturation par défaut</Text>
                      <HelpTooltip
                        title="Toutes les organisations créées seront liées par défaut à ce même compte client."
                        style={{
                          paddingLeft: '10px'
                        }}
                      />
                    </Item>
                    <Item xs={6} justify="flex-start">
                      <Switch
                        checked={defaultStripeCustomerActive}
                        color={themeColors.primary}
                        onChange={handleChange('defaultStripeCustomerActive')}
                      />
                    </Item>
                  </Row>
                  {defaultStripeCustomerActive && (
                    <Row spacing={0} justify="flex-start">
                      <Item
                        xs={5}
                        justify="flex-start"
                        sx={{ marginBottom: 3 }}
                      >
                        <AutocompleteTextField
                          title="compte client"
                          placeholder="Choisissez un compte client"
                          options={customers}
                          formatLabel={option => {
                            return `${option.info.name} (${option._id})`;
                          }}
                          small
                          value={customers.find(
                            el => el._id === defaultStripeCustomerId
                          )}
                          onChange={value =>
                            handleChange('defaultStripeCustomerId')(value?._id)
                          }
                          error={defaultStripeCustomerIdError}
                        />
                      </Item>
                    </Row>
                  )}
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Mes comptes publicitaires" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item justify="flex-start">
                      <ListField
                        values={selectedPlatformAccounts}
                        onChange={handleChangePlatformAccount}
                        actionTxt="Ajouter un compte"
                        deleteAction
                        variant="customFields"
                        isRequireds={[
                          true,
                          true,
                          true,
                          true,
                          true,
                          false,
                          false
                        ]}
                        inputs={[
                          'TextField',
                          'Select',
                          'Select',
                          'TextField',
                          'Select',
                          'Select',
                          'TextField'
                        ]}
                        labels={[
                          'Nom',
                          'Nom de la plateforme',
                          'Connexion',
                          'External ID',
                          'Statut',
                          'Connexion Lié (facultatif)',
                          'External ID Lié (facultatif)'
                        ]}
                        options={[
                          'name',
                          'model',
                          'connexionId',
                          'externalId',
                          'status',
                          'linkConnexionId',
                          'linkExternalId'
                        ]}
                        fieldsFormat={[
                          '',
                          importModels,
                          connectorsConnexions,
                          '',
                          [
                            { label: 'Validé', value: 'validated' },
                            {
                              label: 'En cours de validation',
                              value: 'notValidated'
                            }
                          ],
                          connectorsConnexions,
                          ''
                        ]}
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>

          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Mes pages" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item justify="flex-start">
                      <ListField
                        values={selectedPlatformPages}
                        onChange={handleChangePlatformPage}
                        actionTxt="Ajouter une page"
                        deleteAction
                        variant="customFields"
                        inputs={[
                          'TextField',
                          'Select',
                          'Select',
                          'TextField',
                          'Select'
                        ]}
                        labels={[
                          'Nom',
                          'Media',
                          'Connexion',
                          'External ID',
                          'Statut'
                        ]}
                        options={[
                          'name',
                          'media',
                          'connexionId',
                          'externalId',
                          'status'
                        ]}
                        fieldsFormat={[
                          '',
                          'pageActivableMedia',
                          connectorsConnexions,
                          '',
                          [
                            {
                              label: 'Validé',
                              value: 'validated'
                            },
                            {
                              label: 'En cours de validation',
                              value: 'notValidated'
                            }
                          ]
                        ]}
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection
                title="Partage des audiences CRM"
                tooltip="Tous les utilisateurs présents dans cette section auront accès dans leur module Audience à tous les imports CRM transformés en audiences réalisés sur ce domaine."
                xs={3}
              >
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item justify="flex-start">
                      <ListField
                        options={users.map(formatListFieldUser)}
                        searchOptionsKeys={['email']}
                        values={selectedUsers}
                        onChange={handleChange('selectedUsers')}
                        actionTxt="Ajouter un utilisateur"
                        hideKeys={['_id']}
                        deleteAction
                        variant="asyncAutoComplete"
                        onChangeInput={value => {
                          pollUsers(value);
                        }}
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          {dexemActivated && (
            <>
              <Row spacing={1}>
                <Item justify="flex-start">
                  <FormSection title="Connexion Dexem" xs={3}>
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item justify="flex-start" xs={6}>
                          <Select
                            value={dexemConnectionId}
                            required
                            onChange={handleChange('dexemConnectionId')}
                            title="Sélectionner une connexion Dexem"
                            options={connectorsConnexions.filter(
                              el => el.model === 'dexem'
                            )}
                            small
                          />
                        </Item>
                      </Row>
                    </Container>
                  </FormSection>
                </Item>
              </Row>
              <Row spacing={1}>
                <Item justify="flex-start">
                  <FormSection title="Mails de contact Dexem" xs={3}>
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item justify="flex-start">
                          <ListField
                            values={dexemEmails}
                            onChange={handleChange('dexemEmails')}
                            actionTxt="Ajouter un mail"
                            deleteAction
                            variant="customFields"
                            inputs={['TextField']}
                            labels={['Email']}
                            options={['email']}
                            fieldsFormat={['']}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </FormSection>
                </Item>
              </Row>
            </>
          )}
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Connexion au compte Brevo :" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item xs={3} justify="flex-start">
                      <Text>Campagne courte</Text>
                      <HelpTooltip
                        title="Ce toggle active l'envoi d'email 1j après la fin de la campagne sur les campagnes de moins de 30j"
                        style={{
                          paddingLeft: '10px'
                        }}
                      />
                    </Item>
                    <Item xs justify="flex-start">
                      <Switch
                        checked={shortCampaign}
                        color={themeColors.primary}
                        onChange={handleChange('shortCampaign')}
                      />
                    </Item>
                  </Row>
                  <Row spacing={0} justify="flex-start">
                    <Item xs={3} justify="flex-start">
                      <Text>Campagne longue</Text>
                      <HelpTooltip
                        title="Ce toggle active l'envoi d'email 30j après le début de la campagne sur les campagnes de plus de 30j"
                        style={{
                          paddingLeft: '10px'
                        }}
                      />
                    </Item>
                    <Item xs justify="flex-start">
                      <Switch
                        checked={longCampaign}
                        color={themeColors.primary}
                        onChange={handleChange('longCampaign')}
                      />
                    </Item>
                  </Row>
                  <Row spacing={0} justify="space-between">
                    <Item xs={5}>
                      <TextField
                        title="ApiKey Brevo V2"
                        tooltip="Cette API key permet de se connecter au compte Brevo pour générer les automatisations d'envoi de mail"
                        value={customBrevoV2ApiKey}
                        onChange={handleChange('customBrevoV2ApiKey')}
                      />
                    </Item>
                    <Item xs={5}>
                      <TextField
                        title="ApiKey Brevo V3"
                        tooltip="Cette API key permet de se connecter au compte Brevo pour gérer les transactions Brevo"
                        value={customBrevoV3ApiKey}
                        onChange={handleChange('customBrevoV3ApiKey')}
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Connexion au Getty Image :" xs={3}>
                <Container>
                  <Row spacing={0} justify="space-between">
                    <Item xs={5}>
                      <TextField
                        title="Secret Key"
                        value={gettySecretKey}
                        onChange={handleChange('gettySecretKey')}
                      />
                    </Item>
                    <Item xs={5}>
                      <TextField
                        title="Private Key"
                        value={gettyPrivateKey}
                        onChange={handleChange('gettyPrivateKey')}
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Administrateurs du domain" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item justify="flex-start">
                      <ListField
                        options={users.map(e => formatListFieldUser(e))}
                        searchOptionsKeys={['email']}
                        values={adminUsers}
                        onChange={handleChange('adminUsers')}
                        actionTxt="Ajouter un utilisateur"
                        hideKeys={['_id']}
                        deleteAction
                        variant="asyncAutoComplete"
                        onChangeInput={value => {
                          pollUsers(value);
                        }}
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection
                title="Funnel consolidé"
                tooltip="Ce funnel est consolidé par tous les funnels créés à la validation de campagnes sur ce domaine."
                xs={3}
              >
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item justify="flex-start" xs={6}>
                      <AsyncAutocomplete
                        title="Funnels"
                        fetchOptions={() => fetchFunnels()}
                        small
                        minimunChar={0}
                        width="200px"
                        autoSelect
                        textColor={themeColors.txtHeaderColor}
                        value={selectedFunnel}
                        onChange={data => {
                          setSelectedFunnel(data || null);
                          handleChange('defaultFunnelId')(data?._id);
                        }}
                        placeholder="Funnels"
                        formatLabel={p => {
                          return `${p.name} (${p._id})`;
                        }}
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Ciblage géographique par défaut" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item xs={11} style={{ padding: '0px' }}>
                      <Text>
                        Le ciblage géographique configuré sera appliqué aux
                        campagnes sur les plateformes médias qui nécessitent un
                        ciblage géographique.
                      </Text>
                    </Item>
                    <Item justify="flex-start" xs={6}>
                      <Select
                        title="Zone de chalandise"
                        small
                        value={defaultGeoZoneType}
                        options={geoZones}
                        onChange={value => {
                          handleChange('defaultGeoZoneType')(value);
                        }}
                        width="300px"
                      />
                    </Item>
                    <Item justify="flex-start" xs={6}>
                      <TextField
                        title={
                          geoZones.find(el => el.value === defaultGeoZoneType)
                            ?.label || null
                        }
                        value={defaultGeoZoneValues}
                        onChange={handleChange('defaultGeoZoneValues')}
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Types de ciblage par défaut" xs={3}>
                <Row spacing={0} justify="flex-start">
                  <Item xs={11}>
                    <Text>
                      Définissez, par média, le type de ciblage par défaut à
                      envoyer lors du setup automatique. Cette valeur reste
                      modifiable dans la modale de campagne sur le back-office.
                    </Text>
                  </Item>
                </Row>
                <Row
                  spacing={0}
                  justify="flex-start"
                  style={{ paddingTop: '10px' }}
                >
                  <Item>
                    <DoubleConditionalListField
                      values={defaultTargetingTypesByMedia}
                      onChange={handleChange('defaultTargetingTypesByMedia')}
                      deleteAction={false}
                      firstField={{
                        label: 'Médias',
                        key: 'medias',
                        input: 'MultipleSelect',
                        options: getTargetableMediaOptions()
                      }}
                      secondField={{
                        label: 'Type de ciblage',
                        key: 'targetingType',
                        input: 'Select',
                        getOptions: getTargetingTypeOptionsByMedia
                      }}
                      actionTxt="Ajouter un ciblage par défaut"
                      placeholderTxt="Sélectionner un ou des médias"
                    />
                  </Item>
                </Row>
              </FormSection>
            </Item>
          </Row>
        </Container>
      )}
    </Box>
  );
};

DomainForm.defaultProps = {
  onClose: () => null,
  onCreate: () => null,
  actionName: '',
  title: '',
  saveLabel: '',
  createLabel: 'Créer un nouveau domaine'
};
DomainForm.propTypes = {
  onValidate: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  onCreate: PropTypes.func,
  config: PropTypes.objectOf(PropTypes.any).isRequired,
  actionName: PropTypes.string,
  title: PropTypes.string,
  saveLabel: PropTypes.string,
  createLabel: PropTypes.string,
  activableMedias: PropTypes.arrayOf(PropTypes.any).isRequired
};

export default DomainForm;
